<template>
    <div class="d-flex flex-column overflow-hidden h-100">
        <vue-custom-scrollbar
            class="app-content-padding flex-grow-1 overflow-hidden h-100"
        >
            <ts-page-title
                :title="$t('customerLoanRequest.pageTitle')"
                :breadcrumb="[
                    { text: $t('home'), href: '/' },
                    {
                        text: $t('customerLoanRequest.pageTitle'),
                        href: '/admin/customer-loan-requests'
                    },
                    {
                        text: $t('create'),
                        active: true
                    }
                ]"
            />
            <div class="demo-spin-article">
                <ts-panel>
                    <form-wizard
                        :start-index.sync="activeIndex"
                        color="#00acac"
                        :title="''"
                        :subtitle="''"
                        finishButtonText="Finish"
                        errorColor="red"
                        ref="wizard"
                        @on-complete="onComplete"
                    >
                        <wizard-step
                            slot-scope="props"
                            slot="step"
                            :tab="props.tab"
                            :transition="props.transition"
                            :index="props.index"
                        >
                            <div v-if="model.company_id == 1"
                                slot="active-step"
                                style="display: flex; justify-content:center; align-items: center;"
                            >
                                <lottie
                                    v-if="props.index == 4"
                                    :options="defaultOptions"
                                    :height="65"
                                    :width="65"
                                />
                                <lottie
                                    v-else
                                    :options="defaultOptions2"
                                    :height="65"
                                    :width="65"
                                />
                            </div>
                            <div v-else
                                slot="active-step"
                                style="display: flex; justify-content:center; align-items: center;"
                            >
                                <lottie
                                    v-if="props.index == 6"
                                    :options="defaultOptions"
                                    :height="65"
                                    :width="65"
                                />
                                <lottie
                                    v-else
                                    :options="defaultOptions2"
                                    :height="65"
                                    :width="65"
                                />
                            </div>               
                        </wizard-step>
                        <tab-content
                            style="min-height: 460px !important;"
                            :title="
                                $t('customerLoanRequest.customerInformation')
                            "
                            icon="fas fa-users"
                            :before-change="
                                () => validateStep('customerInformation')
                            "
                        >
                            <customer-information
                                v-model="model"
                                :validation="errors"
                                ref="customerInformation"
                                :clear="clear"
                            />
                        </tab-content>
                        <tab-content
                            style="min-height: 460px !important;"
                            :title="$t('customerLoanRequest.loanInformation')"
                            icon="fas fa-donate"
                            :before-change="validateAndSave"
                        >
                            <loan-information
                                v-model="model"
                                :validation="errors"
                                ref="loanInformation"
                            />
                        </tab-content>
                        <tab-content
                            style="min-height: 460px !important;"
                            :title="$t('customerLoanRequest.attachments')"
                            icon="fas fa-paperclip"
                            :before-change="() => validateAttachmentStep()"
                        >
                            <attachments
                                :request-id="currentRequestId"
                                v-if="currentRequestId"
                            />
                            <div v-else class="text-center p-5">
                                <p class="text-muted">
                                    {{ $t('customerLoanRequest.attachmentNotAvailable') }}
                                </p>
                            </div>
                        </tab-content>
                        <tab-content
                            title="Done"
                            icon=" "
                            style="min-height: 460px !important;"
                        >
                            <lottie
                                v-if="activeIndex == 2"
                                :options="defaultOptions"
                                :height="200"
                                :width="200"
                            />
                            <show />
                        </tab-content>
                        <template slot="footer" slot-scope="props">
                            <div class="wizard-footer-left">
                                <wizard-button
                                    v-if="
                                        props.activeTabIndex == 0 
                                    "
                                    @click.native="$router.push({ name: 'customer-loan-request' })"
                                    :style="'background:#e2e2e2'"
                                    >{{
                                        $t("cancel")
                                    }}</wizard-button
                                >
                            </div>
                            <div class="wizard-footer-left">
                                <wizard-button
                                    v-if="
                                        props.activeTabIndex > 0 &&
                                            !props.isLastStep
                                    "
                                    @click.native="props.prevTab()"
                                    :style="'background:#e2e2e2'"
                                    >{{
                                        $t("customerLoanRequest.previous")
                                    }}</wizard-button
                                >
                            </div>
                            <div class="wizard-footer-right">
                                <wizard-button
                                    v-if="!props.isLastStep"
                                    @click.native="props.nextTab()"
                                    class="wizard-footer-right"
                                    :style="props.fillButtonStyle"
                                    :disabled="nextStep"
                                >
                                    <i
                                        class="fa fa-spinner spin mr-2"
                                        v-if="nextStep"
                                    ></i>
                                    {{ $t("customerLoanRequest.next") }}
                                </wizard-button>
                                <wizard-button
                                    v-else
                                    @click.native="onComplete"
                                    class="wizard-footer-right finish-button"
                                    :style="props.fillButtonStyle"
                                    :disabled="nextStep"
                                >
                                    <i
                                        class="fa fa-spinner spin mr-2"
                                        v-if="nextStep"
                                    ></i>
                                    {{
                                        props.isLastStep
                                            ? $t("customerLoanRequest.done")
                                            : $t("customerLoanRequest.next")
                                    }}
                                </wizard-button>
                            </div>
                        </template>
                    </form-wizard>
                </ts-panel>
            </div>
            <Spin size="large" fix v-if="loading"> </Spin>
        </vue-custom-scrollbar>
    </div>
</template>

<script>
import AppOptions from "@/config/AppOptions.vue";
import Lottie from "vue-lottie";
import animationData from "@/assets/animation.js";
import animationData2 from "@/assets/animation2.js";
import {
    FormWizard,
    WizardStep,
    TabContent,
    WizardButton
} from "vue-form-wizard";
import "vue-form-wizard/dist/vue-form-wizard.min.css";
import { Errors } from "form-backend-validation";
import moment from "moment";
import CustomerInformation from "./components/customer-information.vue";
import LoanInformation from "./components/loan-information.vue";
import Attachments from "./components/attachments.vue";
import Show from "./components/show.vue";
import { mapActions } from "vuex";

export default {
    name: "loanRequestCreate",
    components: {
        Lottie,
        FormWizard,
        TabContent,
        WizardStep,
        WizardButton,
        CustomerInformation,
        LoanInformation,
        Attachments,
        Show
    },
    data() {
        return {
            activeIndex: 0,
            defaultOptions: { animationData: animationData, loop: false },
            defaultOptions2: { animationData: animationData2, loop: false },
            animationSpeed: 1,
            errors: new Errors(),
            loading: false,
            nextStep: false,
            continueNextStep: false,
            clear: false,
            currentRequestId: null,
            model: {
                customer_id: null,
                requested_date: moment().format("DD-MM-YYYY"),
                loan_type_id: null,
                currency_id: null,
                loan_type_cur_id: null,
                group_id: null,
                is_new_cycle: 1,
                cycle_number: 1,
                request_amount: null,
                request_amount_in_word: null,
                request_loan_duration: null,
                request_installment_type_id: null,
                request_interest_rate: null,
                app_amount: null,
                app_loan_duration: null,
                app_installment_type_id: null,
                app_interest_rate: null,
                interval_1_week: null,
                interval_2_week: null,
                interval_1_month: null,
                admin_fee: null,
                cbc_fee: null,
                monthly_collection_fee: null,
                commune_leader_name: null,
                commune_leader_phone: null,
                witness_name: null,
                witness_phone: null,
                other_name: null,
                other_phone: null,
                emp_co_id: null,
                emp_bm_id: null,
                approve_status: null,
                current_app_level: null,
                approval_comment: null,
                attached_request_form: null,
                attached_contact: null,
                purpose_id: null,
                company_id: null,
                request_loan_category_id: null,
                request_collection_location_id: 1
            }
        };
    },
    created() {
        this.model.company_id = this.$store.state.authUser.user.default_branch.company_id
        AppOptions.appContentFullHeight = true;
        AppOptions.appContentClass = "p-0";
    },
    methods: {
        ...mapActions("creditOperation/customerLoanRequest", [
            "getLoanType",
            "getCurrency",
            "getPurpose",
            "getRepaymentMethod",
            "getLoanCategories",
            "getLoanCollectionLocations",
            "validateLoanAmount",
            "find"
        ]),
        validateStep(name) {
            var refToValidate = this.$refs[name];
            return refToValidate.validateForm();
        },
        validateAttachmentStep() {
            // Attachments are optional, always allow proceeding
            return true;
        },
        async validateAndSave() {
            // First validate the form
            var refToValidate = this.$refs['loanInformation'];
            if (!refToValidate.validateForm()) {
                return false;
            }

            // Validate loan amount if required fields are present
            if (this.model.request_loan_category_id && this.model.request_amount && this.model.currency_id) {
                this.nextStep = true;
                try {
                    const response = await this.validateLoanAmount({
                        category_id: this.model.request_loan_category_id,
                        amount: this.model.request_amount,
                        currency_id: this.model.currency_id,
                        locale: this.$root.$i18n.locale === 'kh' ? 'kh' : 'en'
                    });

                    const isValid = response.data.is_valid !== false;
                    const warningMessage = response.data.warning || null;
                    const errorMessage = response.data.message || null;

                    this.nextStep = false;

                    // If is_valid = false, show error alert and block
                    if (!isValid) {
                        this.notice({
                            type: "error",
                            text: errorMessage || "Loan amount validation failed"
                        });
                        return false;
                    }

                    // If warning_message exists, show Yes/No confirm
                    if (warningMessage) {
                        const shouldContinue = await new Promise((resolve) => {
                            this.$Modal.confirm({
                                title: this.$t('customerLoanRequest.pageTitle'),
                                content: warningMessage,
                                okText: this.$t('yes') || 'Yes',
                                cancelText: this.$t('no') || 'No',
                                onOk: () => {
                                    resolve(true);
                                },
                                onCancel: () => {
                                    resolve(false);
                                }
                            });
                        });

                        if (!shouldContinue) {
                            return false;
                        }
                    }
                } catch (error) {
                    this.nextStep = false;
                    // If validation API fails, allow to proceed
                    console.error("Amount validation error:", error);
                }
            }

            // Now save the data
            return await this.onSave();
        },
        async validateLoanInformationStep() {
            // First validate the form
            var refToValidate = this.$refs['loanInformation'];
            if (!refToValidate.validateForm()) {
                return false;
            }

            // Validate loan amount if required fields are present
            if (this.model.request_loan_category_id && this.model.request_amount && this.model.currency_id) {
                this.nextStep = true;
                try {
                    const response = await this.validateLoanAmount({
                        category_id: this.model.request_loan_category_id,
                        amount: this.model.request_amount,
                        currency_id: this.model.currency_id,
                        locale: this.$root.$i18n.locale === 'kh' ? 'kh' : 'en'
                    });

                    const isValid = response.data.is_valid !== false;
                    const warningMessage = response.data.warning || null;
                    const errorMessage = response.data.message || null;

                    this.nextStep = false;

                    // If is_valid = false, show error alert and block
                    if (!isValid) {
                        this.notice({
                            type: "error",
                            text: errorMessage || "Loan amount validation failed"
                        });
                        return false;
                    }

                    // If warning_message exists, show Yes/No confirm
                    if (warningMessage) {
                        return new Promise((resolve) => {
                            this.$Modal.confirm({
                                title: this.$t('customerLoanRequest.pageTitle'),
                                content: warningMessage,
                                okText: this.$t('yes') || 'Yes',
                                cancelText: this.$t('no') || 'No',
                                onOk: () => {
                                    resolve(true);
                                },
                                onCancel: () => {
                                    resolve(false);
                                }
                            });
                        });
                    }

                    // Valid with no warning, proceed normally
                    return true;
                } catch (error) {
                    this.nextStep = false;
                    // If validation API fails, allow to proceed
                    console.error("Amount validation error:", error);
                    return true;
                }
            }

            return true;
        },
        async fetchResource() {
            this.loading = true;
            await this.getLoanType();
            await this.getCurrency();
            await this.getPurpose();
            await this.getRepaymentMethod();
            await this.getLoanCategories();
            await this.getLoanCollectionLocations();
            await this.$refs.customerInformation.loadCustomer();
            await this.$refs.loanInformation.loadEmployee();
            await this.$refs.loanInformation.loadBmEmployee();
            this.setCurrencyToDefault();
            this.loading = false;
        },
        setCurrencyToDefault() {
            this.model.currency_id = this.$store.state.authUser.sys_setting[0].default_currency_id;
        },
        onComplete() {
            this.$refs.wizard.reset();
            this.clearInput();
            this.$router.push({ name: "customer-loan-request" });
        },
        async onSave() {
            this.nextStep = true;
            this.continueNextStep = false;
            this.errors = new Errors();
            this.waiting = true;
            await this.$store
                .dispatch(
                    "creditOperation/customerLoanRequest/store",
                    this.model
                )
                .then(response => {
                    if(response.message_id == 0){
                        // Capture request_id for attachment step
                        this.currentRequestId = response.resource.request_id;

                        this.clear = true;
                        this.waiting = false;
                        this.nextStep = true;
                        this.continueNextStep = true;
                        this.$store.commit(
                        "creditOperation/customerLoanRequest/SET_EDIT_DATA",
                            response.resource
                        );
                    }
                    this.notice({
                        type: response.message_id == 0 ? "success" : "warning",
                        text: response.message
                    });
                })
                .catch(error => {
                    this.waiting = false;
                    this.errors = new Errors(error.errors);
                    this.nextStep = false;
                    this.continueNextStep = false;
                    this.notice({
                        type: "error",
                        text: error.message
                    });
                });
            this.nextStep = false;
            return this.continueNextStep;
        },
        clearInput() {
            this.model.customer_id = null;
            this.model.requested_date = moment().format("DD-MM-YYYY");
            this.model.loan_type_id = null;
            this.model.request_loan_category_id = null;
            this.model.request_collection_location_id = 1;
            this.model.currency_id = null;
            this.model.loan_type_cur_id = null;
            this.model.group_id = null;
            this.model.is_new_cycle = 1;
            this.model.cycle_number = 1;
            this.model.request_amount = null;
            this.model.request_amount_in_word = null;
            this.model.request_installment_type_id = null;
            this.model.request_interest_rate = null;
            this.model.app_amount = null;
            this.model.app_loan_duration = null;
            this.model.app_installment_type_id = null;
            this.model.app_interest_rate = null;
            this.model.interval_1_week = null;
            this.model.interval_2_week = null;
            this.model.interval_1_month = null;
            this.model.admin_fee = null;
            this.model.cbc_fee = null;
            this.model.monthly_collection_fee = null;
            this.model.commune_leader_name = null;
            this.model.commune_leader_phone = null;
            this.model.witness_name = null;
            this.model.witness_phone = null;
            this.model.other_name = null;
            this.model.other_phone = null;
            this.model.emp_co_id = null;
            this.model.emp_bm_id = null;
            this.model.approve_status = null;
            this.model.current_app_level = null;
            this.model.approval_comment = null;
            this.model.attached_request_form = null;
            this.model.attached_contact = null;
            this.model.purpose_id = null;
        },
        notice(not) {
            this.$Notice[not.type]({
                title: "CUSTOMER LOAN REQUEST",
                desc: not.text
            });
        }
    },
    beforeRouteEnter(to, from, next) {
        next(vm => {
            vm.fetchResource();
        });
    },
    beforeRouteLeave(to, from, next) {
        AppOptions.appContentFullHeight = false;
        AppOptions.appContentClass = "";
        this.$store.commit("creditOperation/customerLoanRequest/RESET_STATE");
        this.$store.commit("creditOperation/customerLoanRequest/CLEAR_RESOURCES");
        next();
    }
};
</script>

<!-- Add "scoped" attribute to limit CSS to this component only -->
<style scoped>
a {
    color: #00acac;
}
.wizard-header {
    padding: 5px !important;
    position: relative;
    border-radius: 3px 3px 0 0;
    text-align: center;
}
</style>
